import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './shell/auth/AuthContext'
import { SignalRProvider } from './shell/SignalRContext'
import { ErrorBoundary } from './components/error'
import { Shell } from './shell/Shell'
import { LoginPage } from './shell/auth/LoginPage'
import { ToastProvider } from './components/ui'
import { setLastModule } from './hooks/useLastModule'

// ADR-0035 §11: Route-level code splitting via React.lazy().
const ModuleSelectorPage = lazy(() =>
  import('./modules/ModuleSelectorPage').then((m) => ({
    default: m.ModuleSelectorPage,
  })),
)
// ADR-0069: Module pages.
// Module Status and the standalone API page were dropped from the
// sidebar nav (VP Eng decision, 2026-05-18) — neither had a real
// audience. /modules/{name} now routes through ModuleLandingPage,
// which projects from the capability block (Portal if enabled,
// else Workbench Tools for analysts, else empty state).
//
// Portal vs Workbench → Dashboards (the swap, 2026-05-18): the tabbed
// ModuleDashboards rendering IS the Portal — the customer-facing
// surface. Workbench → Dashboards is the analyst authoring surface
// (placeholder today, builder pending).
const ModuleLandingPage = lazy(() =>
  import('./modules/ModuleLandingPage').then((m) => ({
    default: m.ModuleLandingPage,
  })),
)
const PortalPage = lazy(() =>
  import('./modules/example/PortalPage').then((m) => ({
    default: m.PortalPage,
  })),
)
const DashboardsAuthoringPage = lazy(() =>
  import('./modules/example/DashboardsAuthoringPage').then((m) => ({
    default: m.DashboardsAuthoringPage,
  })),
)
const WorkbenchPage = lazy(() =>
  import('./workbench/WorkbenchPage').then((m) => ({
    default: m.WorkbenchPage,
  })),
)
const AgentChatPage = lazy(() =>
  import('./agents/AgentChatPage').then((m) => ({
    default: m.AgentChatPage,
  })),
)
const ChatHistoryPage = lazy(() =>
  import('./agents/ChatHistoryPage').then((m) => ({
    default: m.ChatHistoryPage,
  })),
)
const WorkspacePage = lazy(() =>
  import('./workspaces/WorkspacePage').then((m) => ({
    default: m.WorkspacePage,
  })),
)
const CodeAssistantPage = lazy(() =>
  import('./agents/CodeAssistantPage').then((m) => ({
    default: m.CodeAssistantPage,
  })),
)
const AccessDeniedPage = lazy(() =>
  import('./shell/AccessDeniedPage').then((m) => ({
    default: m.AccessDeniedPage,
  })),
)
const SecurityDashboardPage = lazy(() =>
  import('./admin/SecurityDashboardPage').then((m) => ({
    default: m.SecurityDashboardPage,
  })),
)
const ReleasesReviewPage = lazy(() =>
  import('./admin/ReleasesReviewPage').then((m) => ({
    default: m.ReleasesReviewPage,
  })),
)
const ObservabilityPage = lazy(() =>
  import('./shell/ObservabilityPage').then((m) => ({ default: m.ObservabilityPage })),
)
const ProfilePage = lazy(() =>
  import('./shell/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const IdeasBoardPage = lazy(() =>
  import('./ideas/IdeasBoardPage').then((m) => ({ default: m.IdeasBoardPage })),
)
const IdeaDetailPage = lazy(() =>
  import('./ideas/IdeaDetailPage').then((m) => ({ default: m.IdeaDetailPage })),
)
const AdminLayout = lazy(() =>
  import('./admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
)
const UsersListPage = lazy(() =>
  import('./admin/UsersListPage').then((m) => ({ default: m.UsersListPage })),
)
const UserDetailPage = lazy(() =>
  import('./admin/UserDetailPage').then((m) => ({ default: m.UserDetailPage })),
)
const RolesListPage = lazy(() =>
  import('./admin/RolesListPage').then((m) => ({ default: m.RolesListPage })),
)
const RoleDetailPage = lazy(() =>
  import('./admin/RoleDetailPage').then((m) => ({ default: m.RoleDetailPage })),
)
const RoleCreatePage = lazy(() =>
  import('./admin/RoleCreatePage').then((m) => ({ default: m.RoleCreatePage })),
)
const AuditEventsPage = lazy(() =>
  import('./audit/AuditEventsPage').then((m) => ({ default: m.AuditEventsPage })),
)
const AuditIntegrityPage = lazy(() =>
  import('./audit/AuditIntegrityPage').then((m) => ({ default: m.AuditIntegrityPage })),
)
const AuditDocsPage = lazy(() =>
  import('./audit/AuditDocsPage').then((m) => ({ default: m.AuditDocsPage })),
)
const AuditTablesPage = lazy(() =>
  import('./audit/AuditTablesPage').then((m) => ({ default: m.AuditTablesPage })),
)
const AuditComparePage = lazy(() =>
  import('./audit/AuditComparePage').then((m) => ({ default: m.AuditComparePage })),
)

// ── Route guards ────────────────────────────────────────────────────────

interface ProtectedRouteProps {
  children: React.ReactNode
}

interface RoleGuardProps extends ProtectedRouteProps {
  requiredRoles: string[]
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return <div className="p-8 text-center text-content-secondary">Loading...</div>
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function RoleGuard({ children, requiredRoles }: RoleGuardProps) {
  const { user } = useAuth()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const userRolesLower = user.roles?.map((r) => r.toLowerCase()) ?? []
  const hasRole = requiredRoles.some((role) => userRolesLower.includes(role.toLowerCase()))

  if (!hasRole) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-content mb-2">Access Denied</h2>
        <p className="text-content-secondary">You do not have permission to view this page.</p>
        <p className="text-sm text-content-tertiary mt-1">
          Required role: {requiredRoles.join(' or ')}
        </p>
      </div>
    )
  }

  return <>{children}</>
}

// ── Environment context tracker ─────────────────────────────────────────

/** Records the last-visited environment for redirect purposes. */
function EnvironmentContextTracker() {
  const { name } = useParams<{ name: string }>()
  useEffect(() => {
    if (name) setLastModule(name)
  }, [name])
  return null
}

// ── App ─────────────────────────────────────────────────────────────────

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <SignalRProvider>
                      <Shell>
                        <ErrorBoundary>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[200px] text-content-secondary">
                                Loading...
                              </div>
                            }
                          >
                            <Routes>
                              {/* ── Top-level routes ──────────────────────── */}
                              <Route
                                path="/"
                                element={
                                  <ErrorBoundary>
                                    <ModuleSelectorPage />
                                  </ErrorBoundary>
                                }
                              />
                              <Route
                                path="/access-denied"
                                element={
                                  <ErrorBoundary>
                                    <AccessDeniedPage />
                                  </ErrorBoundary>
                                }
                              />
                              <Route
                                path="/profile"
                                element={
                                  <ErrorBoundary>
                                    <ProfilePage />
                                  </ErrorBoundary>
                                }
                              />
                              <Route
                                path="/observability"
                                element={
                                  <ErrorBoundary>
                                    <ObservabilityPage />
                                  </ErrorBoundary>
                                }
                              />

                              {/* ── Platform Ideas (DefconAI internal — analyst/admin) ── */}
                              <Route
                                path="/ideas"
                                element={
                                  <RoleGuard requiredRoles={['analyst', 'admin']}>
                                    <ErrorBoundary>
                                      <IdeasBoardPage />
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/ideas/:id"
                                element={
                                  <RoleGuard requiredRoles={['analyst', 'admin']}>
                                    <ErrorBoundary>
                                      <IdeaDetailPage />
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />

                              {/* ── Module routes (ADR-0069) ── */}
                              {/* /modules/:name dispatches via capabilities —
                                  see ModuleLandingPage. Module Status + API
                                  page routes intentionally removed. */}
                              <Route
                                path="/modules/:name"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <ModuleLandingPage />
                                  </ErrorBoundary>
                                }
                              />
                              <Route
                                path="/modules/:name/portal"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <PortalPage />
                                  </ErrorBoundary>
                                }
                              />
                              {/* Legacy plural alias — keeps in-flight links
                                  from /portals working through the rename. */}
                              <Route
                                path="/modules/:name/portals"
                                element={<Navigate to="../portal" replace relative="path" />}
                              />
                              {/* ADR-0069 §2a correction + ADR-0068 (2026-05-18):
                                  Dashboards (authoring), dbt, and Pipeline live
                                  under /modules/:name/workbench/* — they are
                                  authoring surfaces, not customer-facing.
                                  Customers consume the OUTPUT via the Portal.
                                  Old /modules/:name/{dashboards,dbt,pipeline}
                                  paths intentionally removed.
                                  Portal/Dashboards swap (2026-05-18): the tabbed
                                  ModuleDashboards UI moved to /portal; this
                                  Workbench surface is the (future) authoring
                                  builder. */}
                              <Route
                                path="/modules/:name/workbench"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <WorkbenchPage />
                                  </ErrorBoundary>
                                }
                              />
                              <Route
                                path="/modules/:name/workbench/dashboards"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <DashboardsAuthoringPage />
                                  </ErrorBoundary>
                                }
                              />
                              {/* Pipeline tab removed 2026-05-26 — the page exists
                                  but the analyst flow (Jupyter edits → Prefect flows
                                  via worker pool) renders pipeline state through
                                  the existing Dashboards surface. Re-add when there's
                                  a Pipeline-specific UI worth the dedicated nav. */}
                              <Route
                                path="/modules/:name/code-assistant"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <CodeAssistantPage />
                                  </ErrorBoundary>
                                }
                              />
                              {/* AI Assistant — universal-visibility chatbot
                                  (customers + analysts). Server-side
                                  AgentChatAccess + AgentChatModuleMiddleware
                                  enforce per-module grants. */}
                              <Route
                                path="/modules/:name/ai"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <AgentChatPage />
                                  </ErrorBoundary>
                                }
                              />
                              <Route
                                path="/modules/:name/ai/history"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <ChatHistoryPage />
                                  </ErrorBoundary>
                                }
                              />
                              {/* Workspace (ADR-0059) — personal + shared
                                  governed artifacts. Server-side workspace
                                  policies enforce per-module access. */}
                              <Route
                                path="/modules/:name/workspace"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <WorkspacePage />
                                  </ErrorBoundary>
                                }
                              />
                              {/* Module Ideas — same component as platform
                                  Ideas, scoped via useActiveModule(). No
                                  RoleGuard: customers can submit module-
                                  scoped ideas; backend filters by grants. */}
                              <Route
                                path="/modules/:name/ideas"
                                element={
                                  <ErrorBoundary>
                                    <EnvironmentContextTracker />
                                    <IdeasBoardPage />
                                  </ErrorBoundary>
                                }
                              />

                              {/* ── Audit routes ─────────────────────────── */}
                              <Route
                                path="/audit"
                                element={<Navigate to="/audit/events" replace />}
                              />
                              <Route
                                path="/audit/events"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <AuditEventsPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/audit/integrity"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <AuditIntegrityPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/audit/docs"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <AuditDocsPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/audit/tables"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <AuditTablesPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/audit/compare"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <AuditComparePage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />

                              {/* ── Admin routes ─────────────────────────── */}
                              <Route
                                path="/admin"
                                element={<Navigate to="/admin/users" replace />}
                              />
                              <Route
                                path="/admin/users"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <UsersListPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/admin/users/:id"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <UserDetailPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/admin/roles"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <RolesListPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/admin/roles/new"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <RoleCreatePage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/admin/roles/:id"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <RoleDetailPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              <Route
                                path="/admin/security"
                                element={
                                  <RoleGuard requiredRoles={['admin']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <SecurityDashboardPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                              {/* Cross-Domain Release Review — Admin or SecurityLead
                                  (server-side ReleaseReviewAccess is the boundary). */}
                              <Route
                                path="/admin/releases"
                                element={
                                  <RoleGuard requiredRoles={['admin', 'securitylead']}>
                                    <ErrorBoundary>
                                      <AdminLayout>
                                        <ReleasesReviewPage />
                                      </AdminLayout>
                                    </ErrorBoundary>
                                  </RoleGuard>
                                }
                              />
                            </Routes>
                          </Suspense>
                        </ErrorBoundary>
                      </Shell>
                    </SignalRProvider>
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}
